// /contacts — the desktop address book.
//
// This is the SocialTie surface: the place you MANAGE contacts, as opposed to
// the phone tab where you use them. Organising several hundred people is
// keyboard and big-screen work, and the split is by task, not by branding.
// See docs/plan-address-book.md, "Product shape: one system, two surfaces".
//
// Layout is SocialTie's, because it earned it: a filter rail on the left, the
// list in the middle. The rail has two sections and they are NOT the same kind
// of thing — the seven ties are a fixed, code-defined taxonomy, and the lists
// below are freeform and user-created. Same shape as the event-type picker.
//
// Filters are ANY-of within a section and AND across sections, matching the
// server: "Family or Friends" AND "in the Christmas List".

import {
  acceptConnection,
  ApiClientError,
  contactFacets,
  createContact,
  createContactList,
  deleteContact,
  downloadContactsCsv,
  endConnection,
  exportPreview,
  getContact,
  listConnections,
  listContactLists,
  listContacts,
  requestConnection,
  setListMembership,
  updateContact,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  CONTACT_FIELD_KEYS,
  SOCIAL_TIES,
  socialTieLabel,
  type ConnectionDto,
  type ContactDetailDto,
  type ContactFacetsDto,
  type ContactFieldKey,
  type ContactLayer,
  type ContactListDto,
  type ContactSortKey,
  type ContactSummaryDto,
  type SocialTie,
  type SortDirection,
} from '@msc/shared';
import { modal, type ModalHandle } from './modal.js';
import {
  openBulkDelete,
  openBulkList,
  openBulkTies,
  openCreateHousehold,
  openHouseholds,
  type BulkContext,
} from './contactsBulk.js';

/** Field groups for the edit form, in the order they should be filled in. */
const FIELD_GROUPS: { legend: string; keys: readonly (typeof CONTACT_FIELD_KEYS)[number][] }[] = [
  {
    legend: 'Name',
    keys: ['name_title', 'name_given', 'name_preferred', 'name_surname', 'name_suffix'],
  },
  { legend: 'About', keys: ['company', 'born_date'] },
  { legend: 'Phone', keys: ['phone_cell', 'phone_home', 'phone_work'] },
  { legend: 'Email', keys: ['email_personal', 'email_work'] },
  {
    legend: 'Home address',
    keys: [
      'home_address1',
      'home_address2',
      'home_city',
      'home_state',
      'home_zip',
      'home_country',
    ],
  },
  {
    legend: 'Work address',
    keys: [
      'work_address1',
      'work_address2',
      'work_city',
      'work_state',
      'work_zip',
      'work_country',
    ],
  },
];

const FIELD_LABELS: Record<string, string> = {
  name_title: 'Title',
  name_given: 'Given name',
  name_preferred: 'Preferred name',
  name_surname: 'Surname',
  name_suffix: 'Suffix',
  company: 'Company',
  born_date: 'Birthday',
  phone_cell: 'Cell',
  phone_home: 'Home',
  phone_work: 'Work',
  email_personal: 'Personal',
  email_work: 'Work',
  home_address1: 'Address 1',
  home_address2: 'Address 2',
  home_city: 'City',
  home_state: 'State',
  home_zip: 'Zip',
  home_country: 'Country',
  work_address1: 'Address 1',
  work_address2: 'Address 2',
  work_city: 'City',
  work_state: 'State',
  work_zip: 'Zip',
  work_country: 'Country',
};

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key;
}

/**
 * A phone or email value renders as a real `tel:`/`mailto:` link on BOTH
 * surfaces. On the phone that is the entire point of the screen; on a desktop
 * it costs nothing and click-to-call is ordinary now. Everything else is text.
 *
 * The href is stripped to digits because the book is full of "(720)985-8666"
 * and "303.350.7788", and a dialer given punctuation may do nothing at all.
 */
function fieldValue(key: string, value: string): HTMLElement {
  if (key.startsWith('phone_')) {
    const dial = value.replace(/[^\d+]/g, '');
    if (dial !== '') return el('a', { class: 'detail-value', href: `tel:${dial}` }, value);
  }
  if (key.startsWith('email_') && value.includes('@')) {
    return el('a', { class: 'detail-value', href: `mailto:${value}` }, value);
  }
  return el('span', { class: 'detail-value' }, value);
}

export const contactsView: View = async (outlet) => {
  const main = el('main', { class: 'page contacts-page' }, [
    el('p', { class: 'muted' }, 'Loading'),
  ]);
  outlet.append(main);

  // --- filter state --------------------------------------------------------
  const activeTies = new Set<SocialTie>();
  const activeLists = new Set<number>();
  const activeStates = new Set<string>();
  const activeCountries = new Set<string>();
  let search = '';
  let lists: ContactListDto[] = [];
  let facets: ContactFacetsDto = { states: [], countries: [] };

  /**
   * Sort state. Server-side, because the client only ever holds one page and a
   * client-side sort of 483 rows would silently reorder a truncated set -- the
   * top of a 2500-cap page is not the top of the book.
   */
  let sort: ContactSortKey = 'name';
  let direction: SortDirection = 'asc';

  /**
   * The selection, by contact id.
   *
   * KEPT across a sort or a plain refresh, CLEARED whenever the filters or the
   * search change. Sorting does not change which contacts are in view, so
   * clearing there would be gratuitous; filtering does, and a bulk action that
   * silently reaches contacts scrolled out of existence by a filter change is
   * exactly the accident a CRM must not allow.
   */
  const selected = new Set<number>();

  /**
   * Connections, keyed by the contact row they resolve to, for the Status
   * column. A separate call rather than a join on the contacts query: it is one
   * small request for the whole book, it changes on a completely different
   * cadence from the contact rows, and folding it in would put a per-row
   * disclosure question into a query that deliberately reads only the manual
   * layer.
   */
  let connections = new Map<number, ConnectionDto>();
  /** Incoming pending requests, INCLUDING any from people not in the book. */
  let incoming: ConnectionDto[] = [];

  /**
   * The phone surface is a DIFFERENT SET OF TASKS, not a narrower copy of this
   * screen: look someone up, tap to call or text, add someone you just met.
   * Filters, lists, households and export are desktop work and are
   * deliberately ABSENT rather than half-working -- with a pointer to where
   * they live, which is what makes two surfaces feel like one product.
   * See docs/plan-address-book.md, "Two surfaces, split by task and device".
   *
   * 700px is the same breakpoint .contacts-layout collapses at, so the CSS and
   * the JS agree about what "phone" means. They must be changed together.
   */
  const phoneMql = window.matchMedia('(max-width: 700px)');
  const isPhone = (): boolean => phoneMql.matches;
  /** The last page fetched, so a surface switch re-renders without refetching. */
  let lastRows: ContactSummaryDto[] = [];
  /** The phone detail is a sheet, not a pane. Tracked so a switch can close it. */
  let phoneSheet: ModalHandle | null = null;

  async function loadLists(): Promise<void> {
    lists = (await listContactLists()).lists;
  }

  async function loadFacets(): Promise<void> {
    facets = await contactFacets();
  }

  /**
   * Connections are best-effort: a failure here must not take the address book
   * down with it. The Status column degrades to blank, which reads as "not
   * connected" -- the same thing it says for the overwhelming majority of rows
   * anyway -- while every other column stays correct.
   */
  async function loadConnections(): Promise<void> {
    try {
      const page = await listConnections();
      const next = new Map<number, ConnectionDto>();
      for (const c of page.connections) {
        if (c.contactId !== null) next.set(c.contactId, c);
      }
      connections = next;
      // ⚠ KEPT SEPARATELY, and the map above is exactly why. It is keyed by
      // `contactId`, so it silently DROPS every connection that resolves to no
      // contact row -- and an incoming request from somebody who is not in your
      // address book is precisely that case. Keyed alone, a stranger's request
      // could never be rendered anywhere, which is how the accept surface came
      // to be missing without anyone noticing: the one state that most needs a
      // control was the one state the data structure could not represent.
      incoming = page.connections.filter((c) => c.status === 'pending' && !c.outgoing);
    } catch {
      connections = new Map();
      incoming = [];
    }
  }

  try {
    await Promise.all([loadLists(), loadFacets(), loadConnections()]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load your address book.'));
    return;
  }

  // --- elements ------------------------------------------------------------

  /**
   * Incoming SocialTie requests, with the controls to answer them.
   *
   * ⚠ IT RENDERS IN BOTH LAYOUTS, which is the whole reason it is one element
   * built once rather than markup inside `assemble()`. The phone and desktop
   * branches are separate trees, so anything added to only one is invisible to
   * half the users -- the same trap the home page's two modes carry.
   *
   * ⚠ EMPTIED RATHER THAN `hidden`. `replaceChildren()` with no arguments
   * leaves a genuinely childless node, so `.conn-requests:not(:empty)` in the
   * stylesheet is what makes the box appear -- and nothing here sets a `display`
   * that a `hidden` attribute would then lose to. That is a real bug in this
   * codebase's history, and it survived testing because reading `el.hidden` in
   * an assertion is perfectly true while the element is plainly on screen.
   */
  const requestsPanel = el('section', { class: 'conn-requests' }, []);

  function paintRequests(): void {
    if (incoming.length === 0) {
      requestsPanel.replaceChildren();
      return;
    }

    const rows = incoming.map((conn) => {
      // Prefer what the owner calls them; fall back to the account's own name.
      // `contactId` is null for anyone not in the book, which is exactly who
      // this panel exists to show, so the fallback is the normal path here.
      const known = conn.contactId === null ? undefined : lastRows.find(
        (r) => r.contactId === conn.contactId,
      );
      const name = known?.displayName || conn.otherName;

      const problem = el('p', { class: 'form-error', hidden: true });
      const accept = el('button', { type: 'button', class: 'btn' }, 'Accept');
      const decline = el('button', { type: 'button', class: 'btn btn-link' }, 'Decline');

      // One handler for both, because the only difference is which call runs
      // and what to say if it fails. Reloading afterwards rather than patching
      // the row locally: accepting WRITES A CONTACT on both sides, so the book
      // itself has changed and a local edit would leave the table stale.
      const answer = (run: () => Promise<unknown>, failed: string): void => {
        accept.setAttribute('disabled', 'disabled');
        decline.setAttribute('disabled', 'disabled');
        problem.hidden = true;
        void run()
          .then(async () => {
            await Promise.all([loadConnections(), refresh()]);
            paintRequests();
          })
          .catch((err: unknown) => {
            problem.textContent = err instanceof ApiClientError ? err.message : failed;
            problem.hidden = false;
            accept.removeAttribute('disabled');
            decline.removeAttribute('disabled');
          });
      };

      accept.addEventListener('click', () =>
        answer(() => acceptConnection(conn.connectionId), 'Could not accept that request.'));
      decline.addEventListener('click', () => {
        // Confirmed, because a decline cannot be taken back by the person who
        // made it -- they would have to ask again. It is not destructive, so a
        // plain confirm is the right weight.
        if (!window.confirm(`Decline the request from ${name}?`)) return;
        answer(() => endConnection(conn.connectionId), 'Could not decline that request.');
      });

      return el('div', { class: 'conn-request' }, [
        el('div', { class: 'conn-request-who' }, [
          el('p', { class: 'conn-request-name' }, name),
          el(
            'p',
            { class: 'muted small' },
            'Wants to connect. Nothing of yours is shared until you accept, and ' +
              'then only the fields you have chosen to share.',
          ),
          problem,
        ]),
        el('div', { class: 'conn-request-actions' }, [accept, decline]),
      ]);
    });

    requestsPanel.replaceChildren(
      el('h2', {}, incoming.length === 1 ? 'A SocialTie request' : `${incoming.length} SocialTie requests`),
      ...rows,
    );
  }

  const rail = el('aside', { class: 'contacts-rail' }, []);
  const results = el('div', { class: 'contacts-results' }, []);
  const countLabel = el('p', { class: 'muted small' }, '');
  const detail = el('section', { class: 'contact-detail' }, []);

  const searchBox = el('input', {
    type: 'search',
    id: 'contact-search',
    placeholder: 'Search name, company or email',
  }) as HTMLInputElement;

  // Debounced so typing does not fire a request per keystroke.
  let searchTimer: number | undefined;
  searchBox.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      const next = searchBox.value.trim();
      if (next === search) return;
      search = next;
      // Searching changes WHICH contacts are in view, so it drops the
      // selection for the same reason a filter does.
      clearSelection();
      void refresh();
    }, 250);
  });

  /**
   * A filter toggle in the rail: a real checkbox wearing a sliding pill.
   *
   * The knob SLIDES, which means the element has to survive its own state
   * change. The first version called renderRail() from every toggle, which
   * replaced the label with a freshly built one -- so the new knob was simply
   * born at its new position and the CSS transition never had two states to
   * animate between. It read as a control that did not respond at all. The
   * pill therefore owns its own on/off appearance, and the rail is rebuilt
   * only when the set of lists itself changes.
   *
   * Long list names are truncated by CSS, never by storage.
   */
  function pill(label: string, on: boolean, toggle: () => void): HTMLElement {
    const box = el('input', { type: 'checkbox', class: 'pill-toggle' }) as HTMLInputElement;
    box.checked = on;
    const wrap = el('label', { class: `contact-pill${on ? ' on' : ''}`, title: label }, [
      box,
      el('span', { class: 'pill-knob', 'aria-hidden': 'true' }),
      el('span', { class: 'pill-label' }, label),
    ]);
    box.addEventListener('change', () => {
      wrap.classList.toggle('on', box.checked);
      toggle();
    });
    return wrap;
  }

  // Built once and re-used, so a filter change can re-render just THIS box
  // without touching the pills -- rebuilding the rail kills a pill mid-slide.
  const newListButton = el(
    'button',
    { type: 'button', class: 'btn btn-link', id: 'new-list' },
    '+ New list',
  );
  newListButton.addEventListener('click', () => void promptNewList());
  const clearButton = el(
    'button',
    { type: 'button', class: 'btn btn-link', id: 'clear-filters' },
    'Clear filters',
  );
  clearButton.addEventListener('click', () => {
    activeTies.clear();
    activeLists.clear();
    activeStates.clear();
    activeCountries.clear();
    clearSelection();
    renderRail();
    void refresh();
  });
  const railActions = el('div', { class: 'rail-actions' }, []);
  function anyFilter(): boolean {
    return activeTies.size + activeLists.size + activeStates.size + activeCountries.size > 0;
  }

  function syncRailActions(): void {
    railActions.replaceChildren(newListButton, ...(anyFilter() ? [clearButton] : []));
  }

  function renderRail(): void {
    rail.replaceChildren(
      el('h2', { class: 'rail-head' }, 'Filter by'),
      el('h3', { class: 'rail-section' }, 'Social ties'),
      el(
        'div',
        { class: 'pill-group' },
        SOCIAL_TIES.map((tie) =>
          pill(socialTieLabel(tie), activeTies.has(tie), () => {
            if (activeTies.has(tie)) activeTies.delete(tie);
            else activeTies.add(tie);
            syncRailActions();
            void refresh();
          }),
        ),
      ),
      el('h3', { class: 'rail-section' }, 'Lists'),
      lists.length
        ? el(
            'div',
            { class: 'pill-group' },
            lists.map((l) =>
              pill(`${l.name} (${l.memberCount})`, activeLists.has(l.listId), () => {
                if (activeLists.has(l.listId)) activeLists.delete(l.listId);
                else activeLists.add(l.listId);
                clearSelection();
                syncRailActions();
                void refresh();
              }),
            ),
          )
        : el('p', { class: 'muted small' }, 'No lists yet.'),
      ...facetSection('State', facets.states, activeStates),
      ...facetSection('Country', facets.countries, activeCountries),
      railActions,
    );
    syncRailActions();
  }

  /**
   * A facet section: State or Country, built from what is ACTUALLY in the book.
   *
   * Rendered as a `<select multiple>` rather than pills, and that is the one
   * place this rail deliberately breaks its own pattern. The seven ties and the
   * nineteen lists are a browsable set you are meant to read; the countries in
   * this book are 40-odd values with a long tail of one each, and forty pills
   * push the Lists section off the screen. A multi-select is scrollable, keeps
   * its own selected state, and costs one row of rail height regardless of how
   * many values exist.
   *
   * The section is omitted entirely when there are no values, rather than
   * showing an empty control -- a book with no addresses should not carry a
   * State filter at all.
   */
  function facetSection(
    label: string,
    values: readonly { value: string; count: number }[],
    active: Set<string>,
  ): HTMLElement[] {
    if (values.length === 0) return [];
    const id = `facet-${label.toLowerCase()}`;
    const box = el('select', { id, multiple: true, size: String(Math.min(values.length, 6)) }) as HTMLSelectElement;
    for (const v of values) {
      const opt = el('option', { value: v.value }, `${v.value} (${v.count})`) as HTMLOptionElement;
      opt.selected = active.has(v.value);
      box.append(opt);
    }
    box.addEventListener('change', () => {
      active.clear();
      for (const opt of box.selectedOptions) active.add(opt.value);
      clearSelection();
      syncRailActions();
      void refresh();
    });
    return [el('h3', { class: 'rail-section' }, label), box];
  }

  async function promptNewList(): Promise<void> {
    const name = window.prompt('List name');
    if (name === null || name.trim() === '') return;
    try {
      await createContactList(name.trim());
      await loadLists();
      renderRail();
    } catch (err) {
      window.alert(
        err instanceof ApiClientError ? err.message : 'Could not create that list.',
      );
    }
  }

  /**
   * Cell, then home, then work. Returned raw for display and stripped for the
   * href, because `tel:` and `sms:` want digits and the book is full of
   * "(720)985-8666" and "303.350.7788".
   */
  function bestPhone(fields: ContactSummaryDto['fields']): string | null {
    return fields.phone_cell ?? fields.phone_home ?? fields.phone_work ?? null;
  }

  /**
   * A phone row: name, and the two things the phone surface exists to do.
   * Name-only by design -- 307 of Will's 483 contacts have no phone and 87 have
   * nothing but a name, so a second line of "best contact info" is blank more
   * often than not and a five-column table is mostly whitespace.
   *
   * The actions are real links, not buttons calling location.assign: a long
   * press then offers "Add to Contacts" and "Copy", which is free behaviour we
   * would have to rebuild otherwise. A contact with no number gets no actions
   * rather than dead ones.
   */
  function phoneCard(c: ContactSummaryDto): HTMLElement {
    const name = el('button', { type: 'button', class: 'contact-card-name' }, c.displayName);
    name.addEventListener('click', () => void openDetail(c.contactId));

    const num = bestPhone(c.fields);
    const dial = num === null ? '' : num.replace(/[^\d+]/g, '');
    const actions = el('div', { class: 'contact-card-actions' }, [
      ...(dial === ''
        ? []
        : [
            el('a', { class: 'card-action', href: `tel:${dial}`, 'aria-label': `Call ${c.displayName}` }, 'Call'),
            el('a', { class: 'card-action', href: `sms:${dial}`, 'aria-label': `Text ${c.displayName}` }, 'Text'),
          ]),
    ]);

    return el('li', { class: 'contact-card' }, [name, actions]);
  }

  // --- selection -----------------------------------------------------------

  /**
   * What the Status column says about one contact.
   *
   * A connection is between two ACCOUNTS and needs both parties to agree; a
   * social TIE is a private label needing nobody's. They are unrelated, and
   * this column is about the first.
   *
   * The empty string is the answer for the overwhelming majority -- 481 of 483
   * on the real book -- so it renders as nothing at all rather than as "not
   * connected" repeated four hundred times.
   */
  function statusLabel(c: ContactSummaryDto): string {
    const conn = connections.get(c.contactId);
    if (conn === undefined) return c.linkedUserId !== null ? 'Linked' : '';
    if (conn.status === 'accepted') return 'Connected';
    if (conn.status === 'pending') return conn.outgoing ? 'Requested' : 'Wants to connect';
    // declined and revoked stay distinct in the data -- never connected versus
    // connected and ended -- but the column has no room to explain the
    // difference, and the detail pane does.
    return 'Not connected';
  }

  function clearSelection(): void {
    selected.clear();
    syncSelectionUi();
  }

  /**
   * Repaint everything that depends on the selection WITHOUT re-rendering the
   * table.
   *
   * Rebuilding 483 rows on every checkbox tick is both slow and wrong: the row
   * you just clicked is replaced mid-interaction, so focus is lost and a
   * shift-click range has nothing stable to anchor to. The checkboxes own their
   * own state; only the bar and the header box are recomputed.
   */
  function syncSelectionUi(): void {
    const n = selected.size;
    selectionBar.hidden = n === 0;
    selectionCount.textContent = `${n} selected`;

    const visible = lastRows.map((r) => r.contactId);
    const onScreen = visible.filter((id) => selected.has(id)).length;
    // Honest about a selection that reaches beyond what is on screen. It only
    // happens when rows are deleted from under it, since a filter change
    // clears -- but if it ever does, acting on invisible contacts must not be
    // a surprise.
    offScreenNote.hidden = n === onScreen;
    offScreenNote.textContent = `${n - onScreen} not in the current view`;

    if (selectAllBox !== null) {
      selectAllBox.checked = onScreen > 0 && onScreen === visible.length;
      selectAllBox.indeterminate = onScreen > 0 && onScreen < visible.length;
    }
  }

  const selectionCount = el('strong', { class: 'sel-count' }, '0 selected');
  const offScreenNote = el('span', { class: 'muted small' }, '');
  offScreenNote.hidden = true;
  /** Rebuilt with the table, so it is null until the first desktop render. */
  let selectAllBox: HTMLInputElement | null = null;

  function bulkContext(): BulkContext {
    return {
      // A SNAPSHOT, deliberately. A dialog that read the live Set would act on
      // whatever was ticked when Apply was pressed rather than on what its own
      // heading said when it opened.
      selected: [...selected],
      lists,
      refresh,
      reloadLists: async () => {
        await loadLists();
        renderRail();
      },
      clearSelection,
    };
  }

  /**
   * The most common surname in the selection, for the household name box.
   *
   * Right almost every time on a real selection, and wrong in a way that is
   * obvious the moment you read it -- which is the bar a pre-filled value has
   * to clear.
   */
  function suggestedHouseholdName(): string {
    const counts = new Map<string, number>();
    for (const row of lastRows) {
      if (!selected.has(row.contactId)) continue;
      const surname = (row.fields.name_surname ?? '').trim();
      if (surname === '') continue;
      counts.set(surname, (counts.get(surname) ?? 0) + 1);
    }
    let best = '';
    let bestN = 0;
    for (const [name, n] of counts) {
      if (n > bestN) {
        best = name;
        bestN = n;
      }
    }
    return best === '' ? '' : `The ${best}s`;
  }

  const selectionBar = el('div', { class: 'selection-bar' }, []);
  selectionBar.hidden = true;

  function buildSelectionBar(): void {
    const act = (label: string, run: () => void, cls = 'btn btn-secondary') => {
      const b = el('button', { type: 'button', class: cls }, label);
      b.addEventListener('click', run);
      return b;
    };
    const clear = act('Clear', () => {
      clearSelection();
      renderRows(lastRows);
    }, 'btn btn-link');

    selectionBar.replaceChildren(
      el('div', { class: 'sel-info' }, [selectionCount, offScreenNote]),
      el('div', { class: 'sel-actions' }, [
        act('Manage social ties', () => openBulkTies(bulkContext())),
        act('Add to contact lists', () => openBulkList(bulkContext())),
        act('Create household', () =>
          openCreateHousehold(bulkContext(), suggestedHouseholdName()),
        ),
        act('Delete', () => openBulkDelete(bulkContext()), 'btn btn-link btn-danger'),
        clear,
      ]),
    );
  }
  buildSelectionBar();

  // --- rows ----------------------------------------------------------------

  /**
   * A sortable column header.
   *
   * Clicking a new column sorts it ascending; clicking the ACTIVE column
   * flips it. That is the convention every table in every CRM uses, and the
   * arrow is what says which state you are in. `aria-sort` carries the same
   * fact to a screen reader, which cannot see the arrow.
   */
  function sortHeader(label: string, key: ContactSortKey): HTMLElement {
    const active = sort === key;
    const arrow = active ? (direction === 'asc' ? ' \u25b2' : ' \u25bc') : '';
    const th = el(
      'th',
      {
        class: `sortable${active ? ' sorted' : ''}`,
        'aria-sort': active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none',
      },
      [],
    );
    const btn = el('button', { type: 'button', class: 'th-sort' }, `${label}${arrow}`);
    btn.addEventListener('click', () => {
      if (sort === key) direction = direction === 'asc' ? 'desc' : 'asc';
      else {
        sort = key;
        direction = 'asc';
      }
      // Sorting does NOT clear the selection: it changes the order of the rows
      // in view, never which rows they are.
      void refresh();
    });
    th.append(btn);
    return th;
  }

  function renderRows(rows: ContactSummaryDto[]): void {
    lastRows = rows;
    if (rows.length === 0) {
      results.replaceChildren(
        el('p', { class: 'muted' }, 'No contacts match those filters.'),
      );
      selectAllBox = null;
      syncSelectionUi();
      return;
    }
    if (isPhone()) {
      results.replaceChildren(el('ul', { class: 'contact-cards' }, rows.map(phoneCard)));
      selectAllBox = null;
      return;
    }

    // Selects every row CURRENTLY IN VIEW, which is what the box sits above.
    // Not "all 483": with a filter applied those are different sets, and the
    // one the user can see is the one they mean.
    const allBox = el('input', {
      type: 'checkbox',
      class: 'row-check',
      'aria-label': 'Select all contacts in view',
    }) as HTMLInputElement;
    selectAllBox = allBox;
    allBox.addEventListener('change', () => {
      for (const r of rows) {
        if (allBox.checked) selected.add(r.contactId);
        else selected.delete(r.contactId);
      }
      for (const box of results.querySelectorAll<HTMLInputElement>('tbody .row-check')) {
        box.checked = allBox.checked;
      }
      syncSelectionUi();
    });

    results.replaceChildren(
      el(
        'table',
        { class: 'contacts-table' },
        [
          el('thead', {}, [
            el('tr', {}, [
              el('th', { class: 'check-col' }, [allBox]),
              sortHeader('Name', 'name'),
              sortHeader('Company', 'company'),
              sortHeader('Phone', 'phone'),
              sortHeader('Email', 'email'),
              // Ties is NOT sortable: a contact holds several, so there is no
              // single value to order on. It stays a filter, which is what the
              // taxonomy is for.
              el('th', {}, 'Ties'),
              el('th', {}, 'Status'),
            ]),
          ]),
          el(
            'tbody',
            {},
            rows.map((c) => {
              const box = el('input', {
                type: 'checkbox',
                class: 'row-check',
                'aria-label': `Select ${c.displayName}`,
              }) as HTMLInputElement;
              box.checked = selected.has(c.contactId);
              box.addEventListener('change', () => {
                if (box.checked) selected.add(c.contactId);
                else selected.delete(c.contactId);
                syncSelectionUi();
              });
              // The row opens the detail; the checkbox must not. Without this
              // every tick also opens a pane over the table the user is
              // working in.
              box.addEventListener('click', (ev) => ev.stopPropagation());

              const tr = el('tr', { class: 'contact-row', tabindex: '0' }, [
                el('td', { class: 'check-col' }, [box]),
                el('td', {}, [
                  el('span', { class: 'contact-name' }, c.displayName),
                  c.linkedUserId !== null
                    ? el('span', { class: 'muted small linked-badge' }, ' · linked')
                    : el('span', {}, ''),
                ]),
                el('td', {}, c.fields.company ?? ''),
                el('td', {}, c.fields.phone_cell ?? c.fields.phone_home ?? ''),
                el('td', {}, c.fields.email_personal ?? c.fields.email_work ?? ''),
                el('td', {}, c.ties.map(socialTieLabel).join(', ')),
                el('td', { class: 'status-col muted small' }, statusLabel(c)),
              ]);
              const open = () => void openDetail(c.contactId);
              tr.addEventListener('click', open);
              tr.addEventListener('keydown', (ev) => {
                if ((ev as KeyboardEvent).key === 'Enter') open();
              });
              return tr;
            }),
          ),
        ],
      ),
    );
    syncSelectionUi();
  }

  async function refresh(): Promise<void> {
    try {
      const page = await listContacts({
        search: search || undefined,
        ties: [...activeTies],
        lists: [...activeLists],
        states: [...activeStates],
        countries: [...activeCountries],
        sort,
        dir: direction,
        limit: 2500,
      });
      countLabel.textContent = `${page.contacts.length} of ${page.total} contacts`;
      renderRows(page.contacts);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        navigate('/login', true);
        return;
      }
      results.replaceChildren(el('p', { class: 'form-error' }, 'Could not load contacts.'));
    }
  }

  // --- detail --------------------------------------------------------------

  async function openDetail(contactId: number): Promise<void> {
    detail.replaceChildren(el('p', { class: 'muted' }, 'Loading'));
    let c: ContactDetailDto;
    try {
      c = await getContact(contactId);
    } catch {
      detail.replaceChildren(el('p', { class: 'form-error' }, 'Could not load that contact.'));
      return;
    }

    // Show the MERGED value, and say where it came from. "Synced" is the whole
    // pitch of the feature, so the provenance is worth surfacing rather than
    // hiding behind one indistinguishable value.
    const rows = CONTACT_FIELD_KEYS.filter((k) => c.merged[k].value !== null).map((k) =>
      el('div', { class: 'detail-row' }, [
        el('span', { class: 'detail-label muted small' }, fieldLabel(k)),
        fieldValue(k, c.merged[k].value ?? ''),
        c.merged[k].source === 'synced'
          ? el('span', { class: 'synced-badge', title: 'Kept current by them' }, 'synced')
          : el('span', {}, ''),
      ]),
    );

    /**
     * Ties here are OPTIONS, not filters, so they are plain checkboxes. The
     * rail's sliding pills promise "narrow the list down", which is the wrong
     * promise on a control that edits the contact.
     *
     * `next` is read back off the checkboxes rather than derived from the
     * `c.ties` captured at render time: two quick toggles both computed their
     * payload from the same stale array, so the second silently dropped the
     * tie the first had just added.
     */
    const tieBoxes = new Map<SocialTie, HTMLInputElement>();
    const tieCheckboxes = SOCIAL_TIES.map((tie) => {
      const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
      box.checked = c.ties.includes(tie);
      tieBoxes.set(tie, box);
      box.addEventListener('change', () => {
        const next = SOCIAL_TIES.filter((t) => tieBoxes.get(t)?.checked === true);
        void updateContact(c.contactId, { ties: next })
          .then(() => {
            c.ties = next;
            return refresh();
          })
          .catch(() => {
            box.checked = !box.checked;
            window.alert('Could not update ties.');
          });
      });
      return el('label', { class: 'check' }, [box, ` ${socialTieLabel(tie)}`]);
    });

    const listCheckboxes = lists.map((l) => {
      const box = el('input', { type: 'checkbox' }) as HTMLInputElement;
      box.checked = c.listIds.includes(l.listId);
      box.addEventListener('change', () => {
        void setListMembership(l.listId, c.contactId, box.checked)
          .then(loadLists)
          .then(renderRail)
          .catch(() => {
            box.checked = !box.checked;
            window.alert('Could not update that list.');
          });
      });
      return el('label', { class: 'check' }, [box, ` ${l.name}`]);
    });

    /*
     * "Request SocialTie": ask this contact's ACCOUNT to connect.
     *
     * It lives here and NOT as a button in the Status column, deliberately.
     * This sends a real request to a real person -- it is outward-facing, and a
     * one-word control in a dense 483-row table is too easy to hit by accident.
     * The pane has room to say what it does before you press it, and room to
     * explain the answer when it comes back.
     *
     * Whether a contact even HAS an account cannot be known here: the server
     * resolves it by matching their email against site_user_email, so the
     * button is offered for everyone and "they have no account" comes back as a
     * plain sentence rather than as a failure. That is the common case, not an
     * error.
     */
    const conn = connections.get(c.contactId);
    const connectionRow: HTMLElement[] = [];
    if (conn !== undefined) {
      const said =
        conn.status === 'accepted'
          ? 'Connected. Fields they choose to share stay current here on their own.'
          : conn.status === 'pending'
            ? conn.outgoing
              ? 'You asked to connect. Waiting on them.'
              : 'They asked to connect with you.'
            : conn.status === 'declined'
              ? 'A request was declined. You were never connected.'
              : 'You were connected, and it was ended.';
      connectionRow.push(el('h3', {}, 'SocialTie'), el('p', { class: 'muted small' }, said));

      // ⚠ EVERY STATE DESCRIBED HERE USED TO HAVE NO CONTROL. The sentences
      // above have always been right -- "They asked to connect with you." --
      // and there was nothing to press, in any of the four states. Same shape
      // as the tournament play-off control: an endpoint with no control is not
      // a feature, and the API test passing is what let it stay invisible.
      const problem = el('p', { class: 'form-error', hidden: true });
      const act = (
        label: string,
        cls: string,
        run: () => Promise<unknown>,
        confirmWith: string | null,
        failed: string,
      ): HTMLElement => {
        const b = el('button', { type: 'button', class: cls }, label);
        b.addEventListener('click', () => {
          if (confirmWith !== null && !window.confirm(confirmWith)) return;
          b.setAttribute('disabled', 'disabled');
          problem.hidden = true;
          void run()
            .then(async () => {
              // Reload BOTH: accepting writes a contact on each side, so the
              // table changed too, and the panel above must drop the answered
              // request. Then reopen this sheet so it repaints its new state.
              await loadConnections();
              paintRequests();
              await refresh();
              await openDetail(c.contactId);
            })
            .catch((err: unknown) => {
              problem.textContent = err instanceof ApiClientError ? err.message : failed;
              problem.hidden = false;
              b.removeAttribute('disabled');
            });
        });
        return b;
      };

      if (conn.status === 'pending' && !conn.outgoing) {
        connectionRow.push(
          el('div', { class: 'walkin-row' }, [
            act('Accept', 'btn', () => acceptConnection(conn.connectionId), null,
                'Could not accept that request.'),
            act('Decline', 'btn btn-link', () => endConnection(conn.connectionId),
                `Decline the request from ${c.displayName}?`,
                'Could not decline that request.'),
          ]),
        );
      } else if (conn.status === 'pending' && conn.outgoing) {
        // Withdrawing lands on the same `declined` status the recipient's
        // decline does -- one endpoint, and the server picks the verb.
        connectionRow.push(
          act('Withdraw this request', 'btn btn-link', () => endConnection(conn.connectionId),
              `Withdraw your request to ${c.displayName}?`,
              'Could not withdraw that request.'),
        );
      } else if (conn.status === 'accepted') {
        connectionRow.push(
          act('Disconnect', 'btn btn-link btn-danger', () => endConnection(conn.connectionId),
              `Disconnect from ${c.displayName}? Their shared fields stop updating here, ` +
                'and everything you typed yourself is kept.',
              'Could not disconnect.'),
        );
      } else {
        // declined or revoked: a fresh ask is allowed -- a decline is not a
        // block, and the server resets the row rather than refusing.
        const ask = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Ask again');
        ask.addEventListener('click', () => {
          if (!window.confirm(`Ask ${c.displayName} to connect again?`)) return;
          ask.setAttribute('disabled', 'disabled');
          problem.hidden = true;
          void requestConnection(c.contactId)
            .then(async () => {
              await loadConnections();
              paintRequests();
              await openDetail(c.contactId);
            })
            .catch((err: unknown) => {
              problem.textContent =
                err instanceof ApiClientError ? err.message : 'Could not send that request.';
              problem.hidden = false;
              ask.removeAttribute('disabled');
            });
        });
        connectionRow.push(ask);
      }
      connectionRow.push(problem);
    } else {
      const ask = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Request SocialTie');
      ask.addEventListener('click', () => {
        if (
          !window.confirm(
            `Ask ${c.displayName} to connect? They get a request, and nothing is shared unless they accept.`,
          )
        ) {
          return;
        }
        ask.setAttribute('disabled', 'disabled');
        void requestConnection(c.contactId)
          .then(async () => {
            await loadConnections();
            await openDetail(c.contactId);
          })
          .catch((err: unknown) => {
            ask.removeAttribute('disabled');
            window.alert(
              err instanceof ApiClientError
                ? err.message
                : 'Could not send that request.',
            );
          });
      });
      connectionRow.push(
        el('h3', {}, 'SocialTie'),
        el(
          'p',
          { class: 'muted small' },
          'If they have an account, connecting keeps the fields they share up to date here automatically.',
        ),
        ask,
      );
    }

    const editButton = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Edit');
    editButton.addEventListener('click', () => openForm(c));
    const deleteButton = el('button', { type: 'button', class: 'btn btn-link btn-danger' }, 'Delete');
    deleteButton.addEventListener('click', () => {
      if (!window.confirm(`Delete ${c.displayName}?`)) return;
      void deleteContact(c.contactId)
        .then(() => {
          closeDetail();
          return refresh();
        })
        .catch(() => window.alert('Could not delete that contact.'));
    });

    /*
     * LISTS are desktop work and the phone says so instead of shipping a
     * 19-checkbox mail-merge editor onto a 390px screen. TIES stay, because a
     * tie is who someone is to you rather than mail-merge machinery, and the
     * seven of them are the spine of the whole feature. That is the
     * fewer-tasks rule from docs/plan-address-book.md, not a size limit: the
     * phone drops a task and points at where it lives.
     */
    const listSection = isPhone()
      ? [
          el('h3', {}, 'Lists'),
          el(
            'p',
            { class: 'muted small' },
            c.listIds.length === 0
              ? 'Not on any list. Lists are managed on a computer.'
              : `${lists
                  .filter((l) => c.listIds.includes(l.listId))
                  .map((l) => l.name)
                  .join(', ')}. Edit lists on a computer.`,
          ),
        ]
      : [
          el('h3', {}, 'Lists'),
          listCheckboxes.length
            ? el('div', { class: 'check-list' }, listCheckboxes)
            : el('p', { class: 'muted small' }, 'No lists yet.'),
        ];

    const body = el('div', { class: 'detail-body' }, [
      // The sheet already shows the name in its own title bar, so repeating it
      // here would be the name twice on the smallest screen.
      ...(isPhone()
        ? []
        : [el('div', { class: 'detail-head' }, [el('h2', {}, c.displayName)])]),
      c.postalName !== '' && c.postalName !== c.displayName
        ? el('p', { class: 'muted small' }, `Post to: ${c.postalName}`)
        : el('span', {}, ''),
      rows.length ? el('div', { class: 'detail-fields' }, rows) : el('p', { class: 'muted' }, 'No details yet.'),
      c.notes !== null && c.notes !== ''
        ? el('div', { class: 'detail-notes' }, [
            el('h3', {}, 'Private notes'),
            el('p', {}, c.notes),
          ])
        : el('span', {}, ''),
      el('h3', {}, 'Ties'),
      el('div', { class: 'check-list' }, tieCheckboxes),
      ...listSection,
      // Connections are desktop work like lists: the phone surface points at
      // the desktop rather than shipping a half version of a consent flow.
      ...(isPhone() ? [] : connectionRow),
      el('div', { class: 'detail-actions' }, [editButton, deleteButton]),
    ]);

    if (isPhone()) {
      // Rebuilt per open rather than reused: openDetail() is also how the
      // screen refreshes itself after an edit, so a cached sheet would show
      // stale fields.
      closePhoneSheet();
      phoneSheet = modal({ title: c.displayName, body, onClose: closePhoneSheet });
      phoneSheet.open();
      return;
    }
    detail.replaceChildren(body);
  }

  /**
   * modal()'s `destroy()` only removes the overlay -- it does NOT clear
   * `body.modal-open` or re-hide the overlay. Destroying a sheet that is still
   * open therefore leaves the page scroll-locked with nothing on screen to
   * explain it, and leaves that modal's document keydown listener live on a
   * detached node. So: always close, THEN destroy.
   *
   * `phoneSheet` is nulled first because close() fires onClose, which lands
   * back here -- the null makes the re-entry a no-op.
   */
  function closePhoneSheet(): void {
    if (phoneSheet === null) return;
    const sheet = phoneSheet;
    phoneSheet = null;
    sheet.close();
    sheet.destroy();
  }

  /** Clears whichever surface is currently showing the detail. */
  function closeDetail(): void {
    detail.replaceChildren();
    closePhoneSheet();
  }

  // --- create / edit form --------------------------------------------------

  function openForm(existing: ContactDetailDto | null): void {
    const inputs = new Map<string, HTMLInputElement>();
    const groups = FIELD_GROUPS.map((g) =>
      el('fieldset', { class: 'field-group' }, [
        el('legend', {}, g.legend),
        ...g.keys.map((k) => {
          const input = el('input', {
            type: k === 'born_date' ? 'date' : 'text',
            id: `cf-${k}`,
            name: k,
          }) as HTMLInputElement;
          // The EDIT form binds the manual layer, never the merged value.
          // Binding the merge would copy their synced data into your manual
          // layer the first time you saved anything, and it would then stop
          // updating when they changed it.
          input.value = existing?.manual[k] ?? '';
          inputs.set(k, input);
          return el('div', { class: 'field' }, [
            el('label', { for: `cf-${k}` }, fieldLabel(k)),
            input,
          ]);
        }),
      ]),
    );

    const notes = el('textarea', { id: 'cf-notes', rows: 3 }) as HTMLTextAreaElement;
    notes.value = existing?.notes ?? '';

    const error = el('p', { class: 'form-error' }, '');
    error.hidden = true;
    const submit = el(
      'button',
      { type: 'submit', class: 'btn' },
      existing ? 'Save changes' : 'Add contact',
    );

    // method="post" even though JS submits it: if the script fails, the browser
    // must not fall back to GET and put field values in the URL.
    const form = el('form', { method: 'post', class: 'contact-form' }, [
      ...groups,
      el('div', { class: 'field' }, [
        el('label', { for: 'cf-notes' }, 'Private notes'),
        notes,
        el('p', { class: 'muted small' }, 'Only you can ever see these.'),
      ]),
      error,
      el('div', { class: 'form-actions' }, [submit]),
    ]) as HTMLFormElement;

    const dlg = modal({ title: existing ? 'Edit contact' : 'Add a contact', body: form });
    // modal() builds the overlay HIDDEN; nothing shows until open() is called.
    dlg.open();

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      error.hidden = true;
      const fields: ContactLayer = {};
      for (const [k, input] of inputs) {
        const v = input.value.trim();
        // Empty means CLEAR, which the API models as an explicit null. An
        // absent key would mean "leave alone" and the field could never be
        // emptied from this form.
        fields[k as keyof ContactLayer] = v === '' ? null : v;
      }
      const body = { fields, notes: notes.value.trim() || null };
      submit.setAttribute('disabled', 'disabled');
      const done = existing
        ? updateContact(existing.contactId, body)
        : createContact(body);
      void done
        .then(async (saved) => {
          dlg.close();
          await refresh();
          await openDetail(saved.contactId);
        })
        .catch((err: unknown) => {
          error.textContent =
            err instanceof ApiClientError ? err.message : 'Could not save that contact.';
          error.hidden = false;
          submit.removeAttribute('disabled');
        });
    });
  }

  /**
   * The phone surface's quick add: "someone you just met".
   *
   * Four fields, not twenty-four. The full form is the MANAGING task, and it is
   * miserable one-handed standing next to the person whose name you are typing
   * -- which is the exact moment this flow exists for. SocialTie's own left-rail
   * quick add is the prior art and it is these same four.
   *
   * What is deliberately NOT here, each for a different reason:
   *  - The other twenty fields. Company, addresses and birthdays are things you
   *    look up later, not things you are told on meeting someone. The detail
   *    sheet opens on save and its Edit button is the full form.
   *  - An "add to list" dropdown, despite SocialTie having one. Lists are
   *    desktop work on this surface and the detail sheet already SAYS so; a
   *    dropdown here is the one place the phone would contradict itself.
   *  - The seven ties. You rarely know one on meeting someone, and the sheet
   *    that opens on save carries all seven already.
   *
   * Only the first name is required, and only in the browser: POST /contacts
   * accepts a contact with no fields at all and renders it "Unnamed contact".
   */
  function openQuickAdd(): void {
    /*
     * Both `type` and `inputmode`, because they are read by different things:
     * `type=tel` is what gets a dialer keypad, `inputmode` is the finer hint,
     * and `autocomplete` is what offers the phone's own saved values.
     *
     * `autocapitalize: 'none'` on the email is not cosmetic -- iOS capitalises
     * the first letter of a text field, and a capitalised local part is a real
     * address that silently goes to nobody.
     *
     * Every value here is a STRING, including 'false'. el() drops a boolean
     * false attribute entirely, so `spellcheck: false` would render no
     * attribute at all rather than `spellcheck="false"`.
     */
    const spec: {
      key: ContactFieldKey;
      label: string;
      attrs: Record<string, string | boolean>;
    }[] = [
      {
        key: 'name_given',
        label: 'First name',
        attrs: {
          type: 'text',
          autocomplete: 'given-name',
          autocapitalize: 'words',
          required: true,
        },
      },
      {
        key: 'name_surname',
        label: 'Last name',
        attrs: { type: 'text', autocomplete: 'family-name', autocapitalize: 'words' },
      },
      {
        key: 'phone_cell',
        label: 'Cell',
        attrs: { type: 'tel', inputmode: 'tel', autocomplete: 'tel' },
      },
      {
        key: 'email_personal',
        label: 'Email',
        attrs: {
          type: 'email',
          inputmode: 'email',
          autocomplete: 'email',
          autocapitalize: 'none',
          autocorrect: 'off',
          spellcheck: 'false',
        },
      },
    ];

    const inputs = new Map<ContactFieldKey, HTMLInputElement>();
    const fields = spec.map((f) => {
      const input = el('input', {
        id: `qa-${f.key}`,
        name: f.key,
        ...f.attrs,
      }) as HTMLInputElement;
      inputs.set(f.key, input);
      return el('div', { class: 'field' }, [
        el('label', { for: `qa-${f.key}` }, f.label),
        input,
      ]);
    });

    const error = el('p', { class: 'form-error' }, '');
    error.hidden = true;
    const submit = el('button', { type: 'submit', class: 'btn' }, 'Add contact');

    // method="post" even though JS submits it: if the script fails, the browser
    // must not fall back to GET and put a phone number in the URL.
    const form = el('form', { method: 'post', class: 'quick-add-form' }, [
      ...fields,
      // The pointer, not an apology. Says the missing fields are deferred
      // rather than unavailable, which is what "fewer tasks" has to feel like.
      el(
        'p',
        { class: 'muted small' },
        'Just the basics. Everything else can be filled in later.',
      ),
      error,
      el('div', { class: 'form-actions' }, [submit]),
    ]) as HTMLFormElement;

    // Destroyed on EVERY close route, not just the successful one. The order
    // matters and is the reverse of what it looks like: destroy() alone removes
    // the overlay while leaving body.modal-open set, so it must run from inside
    // onClose -- which fires after close() has already cleared that state.
    const dlg = modal({
      title: 'Add a contact',
      body: form,
      onClose: () => dlg.destroy(),
    });
    dlg.open();
    /*
     * modal() focuses its own × before anything in the body -- its comment
     * claims the first field, but the close button is earlier in the DOM and
     * wins the querySelector. On a form you opened in order to TYPE, and on a
     * phone where this is what decides whether the keyboard comes up at all,
     * the first field is the only sane target.
     *
     * Fixed here and not in modal(), deliberately: the contact detail sheet is
     * a READING surface where × genuinely is the right target, and a blanket
     * change would drop focus onto a tie checkbox there instead.
     */
    inputs.get('name_given')?.focus();

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      error.hidden = true;
      // An absent key is right here and would be WRONG in the edit form, which
      // sends an explicit null to clear. There is nothing to clear on a contact
      // that does not exist yet, and an empty string would store a blank value
      // that reads as "known to be empty".
      const layer: ContactLayer = {};
      for (const [k, input] of inputs) {
        const v = input.value.trim();
        if (v !== '') layer[k] = v;
      }
      submit.setAttribute('disabled', 'disabled');
      void createContact({ fields: layer })
        .then(async (saved) => {
          dlg.close();
          await refresh();
          // The confirmation IS the sheet: it shows what got saved, and it is
          // where ties and the full form live for the next thirty seconds while
          // you still remember what they told you.
          await openDetail(saved.contactId);
        })
        .catch((err: unknown) => {
          error.textContent =
            err instanceof ApiClientError ? err.message : 'Could not save that contact.';
          error.hidden = false;
          submit.removeAttribute('disabled');
        });
    });
  }


  // --- export --------------------------------------------------------------
  //
  // The Christmas card run. Exports MERGED values, so an address a contact
  // maintains themselves is the one that reaches the mail merge -- which is the
  // entire reason to keep the book current.

  function openExport(): void {
    const scope = el('select', { id: 'ex-scope' }) as HTMLSelectElement;
    scope.append(
      el('option', { value: 'mailing' }, 'Mailing list (for a mail merge)'),
      el('option', { value: 'full' }, 'Full backup (every field)'),
    );

    const which = el('select', { id: 'ex-address' }) as HTMLSelectElement;
    which.append(
      el('option', { value: 'home' }, 'Home addresses'),
      el('option', { value: 'work' }, 'Work addresses'),
    );

    const scopeNote = el('p', { class: 'muted small' }, '');
    const counts = el('p', { class: 'muted small' }, 'Counting…');
    const addressRow = el('div', { class: 'field' }, [
      el('label', { for: 'ex-address' }, 'Which address'),
      which,
    ]);

    const selected = [...activeLists];
    const scopeLabel =
      selected.length > 0
        ? `${selected.length} selected list${selected.length === 1 ? '' : 's'}`
        : 'your whole address book';

    async function recount(): Promise<void> {
      try {
        const p = await exportPreview(selected);
        counts.textContent =
          scope.value === 'full'
            ? `${p.total} contacts will be exported.`
            : `${p.postable} of ${p.total} have a usable address. ` +
              `${p.missing} will be left out.`;
      } catch {
        counts.textContent = 'Could not count those contacts.';
      }
    }

    function syncScope(): void {
      const full = scope.value === 'full';
      addressRow.hidden = full;
      scopeNote.textContent = full
        ? 'Everything, including contacts with no address. This is the one to keep.'
        : 'Contacts with no street and city are left out, so you do not print blank envelopes.';
      void recount();
    }
    scope.addEventListener('change', syncScope);
    which.addEventListener('change', () => void recount());

    const error = el('p', { class: 'form-error' }, '');
    error.hidden = true;
    const go = el('button', { type: 'submit', class: 'btn' }, 'Download CSV');

    const form = el('form', { method: 'post', class: 'export-form' }, [
      el('p', {}, `Exporting ${scopeLabel}.`),
      el('div', { class: 'field' }, [el('label', { for: 'ex-scope' }, 'What to export'), scope]),
      addressRow,
      scopeNote,
      counts,
      error,
      el('div', { class: 'form-actions' }, [go]),
    ]) as HTMLFormElement;

    const dlg = modal({ title: 'Export contacts', body: form });
    dlg.open();
    syncScope();

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      error.hidden = true;
      go.setAttribute('disabled', 'disabled');
      void downloadContactsCsv({
        lists: selected,
        address: which.value === 'work' ? 'work' : 'home',
        scope: scope.value === 'full' ? 'full' : 'mailing',
      })
        .then(() => dlg.close())
        .catch((err: unknown) => {
          error.textContent =
            err instanceof ApiClientError ? err.message : 'Could not build that export.';
          error.hidden = false;
          go.removeAttribute('disabled');
        });
    });
  }

  // --- assemble ------------------------------------------------------------
  const addButton = el('button', { type: 'button', class: 'btn' }, '+ Add contact');
  /*
   * isPhone() is read at CLICK time, not here. This button is built once and
   * re-used across every assemble(), so binding the surface now would leave a
   * phone that had been rotated from a desktop width still opening the
   * twenty-four-field form.
   */
  addButton.addEventListener('click', () => {
    if (isPhone()) openQuickAdd();
    else openForm(null);
  });
  const exportButton = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Export');
  exportButton.addEventListener('click', () => openExport());
  const householdsButton = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Households');
  householdsButton.addEventListener('click', () => openHouseholds(bulkContext()));

  /**
   * Two surfaces over one view, one route and one API. The phone gets three
   * jobs -- look someone up, tap to call or text, add someone you just met --
   * and says out loud where the rest lives. Everything it omits (the tie and
   * list filters, export, the side-by-side detail) is desktop work by decision,
   * not by screen budget: docs/plan-address-book.md, "simple means FEWER TASKS,
   * never worse versions of the same ones".
   */
  function assemble(): void {
    // Painted HERE rather than only after an action, which is the bug the first
    // draft shipped: the panel was built, mounted in both layouts and styled,
    // and stayed empty on load because nothing ever filled it. It looked
    // exactly like having no requests. assemble() also runs on a surface
    // switch, so the phone and desktop trees each get a painted panel.
    paintRequests();
    if (isPhone()) {
      main.replaceChildren(
        el('div', { class: 'page-head' }, [
          el('h1', {}, 'Address book'),
          el('div', { class: 'page-head-actions' }, [addButton]),
        ]),
        el('div', { class: 'contacts-phone' }, [
          // Above search, deliberately. A request is somebody waiting on an
          // answer, which outranks a lookup; and it self-hides when empty, so
          // it costs the common case nothing.
          requestsPanel,
          // Search first, and it is the only filter here: with 483 contacts,
          // typing three letters beats 26 toggles on a touch screen.
          el('div', { class: 'contacts-toolbar' }, [searchBox, countLabel]),
          // Above the list, not below it. Below 483 rows is not a pointer,
          // it is a secret.
          el(
            'p',
            { class: 'surface-note muted small' },
            'Filters, lists, households and CSV export are on the desktop version of this page.',
          ),
          results,
        ]),
      );
      return;
    }
    renderRail();
    main.replaceChildren(
      el('div', { class: 'page-head' }, [
        el('h1', {}, 'Address book'),
        el('div', { class: 'page-head-actions' }, [
          householdsButton,
          exportButton,
          addButton,
        ]),
      ]),
      requestsPanel,
      el('div', { class: 'contacts-layout' }, [
        rail,
        el('div', { class: 'contacts-main' }, [
          el('div', { class: 'contacts-toolbar' }, [searchBox, countLabel]),
          // Between the toolbar and the table, so it appears exactly where the
          // eye already is when a box is ticked -- and it is `hidden` until
          // something is selected rather than being an always-present bar with
          // five disabled buttons.
          selectionBar,
          results,
        ]),
        detail,
      ]),
    );
  }

  /*
   * Rotating a phone, or dragging a desktop window narrow, crosses the
   * breakpoint mid-session. Re-assembling on the change keeps the DOM honest;
   * re-rendering from `lastRows` avoids a refetch of 270KB just to change
   * shape. The open detail is closed rather than migrated between a sheet and
   * a pane -- and it MUST be closed, or a sheet left open across the switch
   * strands `body.modal-open` on a page that no longer has a sheet.
   *
   * The filters are deliberately NOT cleared: a filter set on the desktop
   * still applies to the count the phone shows, so silently dropping it would
   * make the same book report two different sizes.
   */
  phoneMql.addEventListener('change', () => {
    closeDetail();
    // The phone surface has no checkboxes and no action bar, so a selection
    // made on the desktop would survive as state with nothing on screen
    // representing it -- and reappear, still ticked, on the way back.
    clearSelection();
    assemble();
    if (lastRows.length > 0) renderRows(lastRows);
  });

  assemble();

  await refresh();
};
